import React, { useMemo } from "react"
import { Table, Space, Tag, Button, message, Typography } from "@mimber/ui-web"
import { usePayouts } from "@mimber/client/mimber-organization"
import type { ColumnsType } from "@mimber/ui-web"
import type { PayoutDto } from "@mimber/schema"
import type { PayoutsTableProps } from "./PayoutsTable.types"
import { format, getUnixTime, parseISO } from "date-fns"

const { Text } = Typography

export const PayoutsTable: React.FunctionComponent<PayoutsTableProps> = ({
  dateRange,
  onViewTransactions,
  className,
}) => {
  const { data, isLoading, error } = usePayouts(dateRange)

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load payouts: ${error.message}`)
    }
  }, [error])

  const payouts = data?.data || []

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100)
  }

  const columns: ColumnsType<PayoutDto> = useMemo(
    () => [
      {
        title: "Arrival Date",
        dataIndex: "arrivalDate",
        key: "arrivalDate",
        render: (date: string) => format(parseISO(date), "MMM d, yyyy"),
        sorter: (a: PayoutDto, b: PayoutDto) =>
          getUnixTime(parseISO(a.arrivalDate)) -
          getUnixTime(parseISO(b.arrivalDate)),
      },
      {
        title: "Amount",
        key: "amount",
        render: (_: unknown, record: PayoutDto) => (
          <Text strong>{formatCurrency(record.amount, record.currency)}</Text>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: string) => {
          const colors: Record<string, string> = {
            paid: "success",
            pending: "processing",
            in_transit: "warning",
            canceled: "default",
            failed: "error",
          }
          return (
            <Tag color={colors[status] || "default"}>
              {status.toUpperCase()}
            </Tag>
          )
        },
      },
      {
        title: "Destination",
        key: "destination",
        render: (_: unknown, record: PayoutDto) => (
          <Space orientation="vertical" size={0}>
            {record.bankName && <Text>{record.bankName}</Text>}
            {record.bankAccountLast4 && (
              <Text type="secondary">•••• {record.bankAccountLast4}</Text>
            )}
          </Space>
        ),
      },
      {
        title: "Method",
        dataIndex: "method",
        key: "method",
        render: (method: string) => method.toUpperCase(),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_: unknown, record: PayoutDto) => (
          <Button
            type="link"
            size="small"
            onClick={() => onViewTransactions?.(record.id)}
          >
            View Transactions
          </Button>
        ),
      },
    ],
    [onViewTransactions]
  )

  return (
    <Table
      dataSource={payouts}
      columns={columns}
      rowKey="id"
      loading={isLoading}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} payouts`,
      }}
      className={className}
    />
  )
}
